import API from '@/Core.Service.Domain/Controls/Controls.Common/API';
import VAVApi from '@/Core.Service.Domain/Controls/VAVs/API';
import AHUApi from '@/Core.Service.Domain/Controls/AHUs/API';
import router from '@/router';

import { RemapObjectKeysAdapter } from '@/Core.Patterns/Adapter';

// Portfolio sitedata flags. Daikin (type 4) has no flag, so it stays unfiltered.
const PORTFOLIO_FLAG_BY_CONTROL_TYPE_ID = {
  2: 'hasThermostat',
  3: 'hasPowerControl',
};

const siteIdOf = (site) => site?.siteId ?? site?.id;

// Exported for tests; it is not a store action.
export const filterSitesByPortfolioFlag = (sites, portfolioSites, flag) => {
  const allowedIds = new Set(
    portfolioSites.filter((site) => site[flag]).map(siteIdOf)
  );
  return sites.filter((site) => allowedIds.has(siteIdOf(site)));
};

// The controls site list includes every site with any control. Thermostat and
// Power Control pages should only offer sites that actually have that equipment.
// If the portfolio fetch fails, keep the unfiltered list so the dropdown is not empty.
const keepSitesWithPortfolioFlag = async (context, sites, flag) => {
  const loadedGetter =
    'ViewSupportStore/PortfolioStore/getPortfolioSitesLoaded';
  const listGetter = 'ViewSupportStore/PortfolioStore/getPortfolioSitesList';

  if (!context.rootGetters[loadedGetter]) {
    await context.dispatch(
      'ViewSupportStore/PortfolioStore/getPortfolioSites',
      null,
      { root: true }
    );
  }

  if (!context.rootGetters[loadedGetter]) {
    return sites;
  }

  const portfolioSites = context.rootGetters[listGetter] || [];
  return filterSitesByPortfolioFlag(sites, portfolioSites, flag);
};

// Counts the calls to getSitesWithControls, so an answer that arrives after a
// newer call has started can be recognised and dropped.
let latestSitesRequest = 0;

// Resolves true when its list was stored, false when a newer call took over.
const getSitesWithControls = async (context) => {
  latestSitesRequest += 1;
  const request = latestSitesRequest;

  const isRouteVAV = router.currentRoute.value.name === 'VAVsPortfolio';
  const isRouteAHU = router.currentRoute.value.name === 'AHUsPortfolio';
  let sitesWithControls;
  if (isRouteVAV) {
    sitesWithControls = await VAVApi.getSitesWithVAVs();
    sitesWithControls.forEach((site) => {
      RemapObjectKeysAdapter(site, ['siteId', 'siteName'], ['id', 'name']);
    });
  } else if (isRouteAHU) {
    sitesWithControls = await AHUApi.getSitesWithAHUs();
    sitesWithControls.forEach((site) => {
      RemapObjectKeysAdapter(site, ['siteId', 'siteName'], ['id', 'name']);
    });
  } else {
    sitesWithControls = await API.getSitesWithControls();
    // Read the route only now, on purpose: the list is for the page the user
    // is on when the answer arrives, not the one they were on when it was
    // asked for. A move to another control page also starts a newer call,
    // which takes over below.
    const flag =
      PORTFOLIO_FLAG_BY_CONTROL_TYPE_ID[
        router.currentRoute.value.meta.controlTypeId
      ];
    if (flag && Array.isArray(sitesWithControls)) {
      sitesWithControls = await keepSitesWithPortfolioFlag(
        context,
        sitesWithControls,
        flag
      );
    }
  }

  // The user moved to another control page while this was loading, and that
  // page has asked for its own list: this answer must not replace it.
  if (request !== latestSitesRequest) return false;

  context.commit('SITES_WITH_CONTROLS', sitesWithControls);
  return true;
};

const getControlsBySite = async (context, site) => {
  const { selectedSite } = site;
  const { controlTypeName } = site;
  const { controlTypeId } = site;

  const selectedSiteControls = await API.getControlsBySite(selectedSite);
  const selectedSiteControlsWithType = selectedSiteControls.filter((site) => {
    if (Array.isArray(controlTypeId)) {
      return controlTypeId.includes(site.controlTypeId);
    }

    return (
      site.controlTypeId === controlTypeId ||
      site.controlTypeName === controlTypeName
    );
  });
  context.commit('SELECTED_SITE_CONTROLS', selectedSiteControlsWithType);
};

const setControlCurrentRoute = async (context, controlCurrentRoute) => {
  context.commit('CONTROL_CURRENT_ROUTE', controlCurrentRoute);
};

const setControlsSettingsHistoryMessagesTimerOn = async (context, timerOn) => {
  context.commit('CONTROL_SETTINGS_HISTORY_TIMER_ON', timerOn);
};

export default {
  getSitesWithControls,
  getControlsBySite,
  setControlCurrentRoute,
  setControlsSettingsHistoryMessagesTimerOn,
};
